/**
 * useProcessTests — per-process test results for the TestsPanel (P7).
 *
 * Runs the engine `runTests` core (with coverage) over the resolved model once
 * per model change, then narrows to the active process: the tests targeting it
 * (zipped back to their source `TestDecl` so a click can replay their pins) and
 * that process's step-coverage entry. Pure derivation — no playback side effects.
 */

import { useMemo } from "react";
import {
  runTests,
  collectTests,
  type ProcessModel,
  type ResolvedModel,
  type TestDecl,
  type TestRunResult,
  type ProcessCoverage,
} from "@archlang/engine";

export interface ProcessTestEntry {
  readonly decl: TestDecl;
  readonly result: TestRunResult;
}

export interface ProcessTests {
  readonly entries: readonly ProcessTestEntry[];
  readonly coverage: ProcessCoverage | null;
}

const EMPTY: ProcessTests = { entries: [], coverage: null };

export function useProcessTests(
  model: ResolvedModel | null,
  activeProcess: ProcessModel | null,
): ProcessTests {
  return useMemo(() => {
    if (!model || !activeProcess) return EMPTY;
    const report = runTests(model, { coverage: true });
    const decls = collectTests(model);
    // Zip each result back to its decl (by id, else name + owner) so pin replay
    // has the source; prefer the stable id to survive same-name collisions.
    const declFor = (r: TestRunResult): TestDecl | undefined =>
      (r.id ? decls.find((d) => d.id === r.id) : undefined) ??
      decls.find(
        (d) =>
          d.name === r.name &&
          (r.ownerPath === undefined || (d.ownerPath ?? []).join(".") === r.ownerPath.join(".")),
      );
    // Match tests to THIS process by fully-qualified path, not bare name — two
    // processes named `Booking` under different owners must not cross-leak.
    const activePath = [...(activeProcess.ownerPath ?? []), activeProcess.name].join(".");
    const entries: ProcessTestEntry[] = [];
    for (const r of report.results) {
      if ((r.targetPath ?? []).join(".") !== activePath) continue;
      const decl = declFor(r);
      if (decl) entries.push({ decl, result: r });
    }
    const coverage =
      report.coverage?.perProcess.find((p) => p.process === activeProcess.name) ?? null;
    return { entries, coverage };
  }, [model, activeProcess]);
}
